import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  PlusIcon,
  TrashIcon,
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  SparklesIcon,
  Squares2X2Icon,
  UserGroupIcon,
  ListBulletIcon,
  ArrowRightIcon,
  XMarkIcon,
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

import type {
  Project,
  Candidate,
  Criterion,
  Evaluation,
  UploadedFile,
} from '../types';
import {
  computeStats,
  formatRelative,
  getLeafCriteriaIds,
  formatFileSize,
  buildCriterionColorMap,
} from '../utils';
import type { CriterionColor } from '../utils';
import { REFERENCE_NOW_ISO } from '../mocks';
import StatusBadge from '../components/StatusBadge';
import MockPdfViewer from '../components/MockPdfViewer';
import type { CitationMeta } from '../components/MockPdfViewer';
import EvaluationEditor from '../components/EvaluationEditor';
import AllCitationsPanel from '../components/AllCitationsPanel';
import CriteriaOverlay from '../components/CriteriaOverlay';

interface Props {
  project: Project;
  onBack: () => void;
  onDelete: () => void;
  // Fichiers
  onAddRegulationFiles: (files: File[]) => void;
  onAddCandidate: (name: string) => void;
  onAddCandidateFiles: (candidateId: string, files: File[]) => void;
  onRemoveFile: (fileId: string) => void;
  onRemoveCandidate: (candidateId: string) => void;
  // Critères
  onCreateCriterion: (c: Omit<Criterion, 'id' | 'order'>) => void;
  onUpdateCriterion: (id: string, patch: Partial<Criterion>) => void;
  onDeleteCriterion: (id: string) => void;
  onToggleCriterionActive: (id: string, active: boolean) => void;
  onReextractCriteria: () => void;
  onValidateCriteria: () => void;
  // Évaluations
  onUpdateEvaluation: (candidateId: string, criterionId: string, patch: Partial<Evaluation>) => void;
  onAddCitation: (evaluationId: string, text: string, sourceFileId: string, sourcePage: number) => void;
  onVerifyCitation: (citationId: string, verified: boolean) => void;
  onBulkVerifyCitations: (citationIds: string[], verified: boolean) => void;
  onRemoveCitation: (citationId: string) => void;
  // Actions IA / export (stubs)
  onRunAiScoring: () => void;
  onExportRao: () => void;
}

/**
 * Workspace unifié d'un projet d'analyse.
 *
 * Tout se passe sur une seule page :
 *  - barre supérieure (navigation + actions projet)
 *  - bandeau extraction (conditionnel, seulement si critères non validés)
 *  - rail candidats (tabs)
 *  - rail critères (chips)
 *  - zone centrale = PDF viewer + éditeur d'évaluation
 *
 * Les états vides progressifs guident l'analyste dans l'onboarding :
 *   1. Pas de RC → invite au dépôt
 *   2. RC mais pas de critères → invite à l'extraction
 *   3. Critères non validés → bandeau + overlay critères auto
 *   4. Pas de candidats → invite au dépôt de mémoires
 *   5. Tout prêt → workspace pleine puissance
 */
const ProjectWorkspace: React.FC<Props> = ({
  project,
  onBack,
  onDelete,
  onAddRegulationFiles,
  onAddCandidate,
  onAddCandidateFiles,
  onRemoveFile,
  onRemoveCandidate,
  onCreateCriterion,
  onUpdateCriterion,
  onDeleteCriterion,
  onToggleCriterionActive,
  onReextractCriteria,
  onValidateCriteria,
  onUpdateEvaluation,
  onAddCitation,
  onVerifyCitation,
  onBulkVerifyCitations,
  onRemoveCitation,
  onRunAiScoring,
  onExportRao,
}) => {
  const stats = useMemo(() => computeStats(project), [project]);
  const leafIds = useMemo(() => getLeafCriteriaIds(project.criteria), [project.criteria]);
  const activeLeafCriteria = useMemo(
    () =>
      project.criteria
        .filter(c => leafIds.includes(c.id) && c.active)
        .sort((a, b) => a.code.localeCompare(b.code, 'fr', { numeric: true })),
    [project.criteria, leafIds]
  );

  // Overlays
  const [criteriaOverlayOpen, setCriteriaOverlayOpen] = useState(false);
  const [rcOverlayOpen, setRcOverlayOpen] = useState(false);
  const [rankingOverlayOpen, setRankingOverlayOpen] = useState(false);
  const [candidateModalOpen, setCandidateModalOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Ouverture auto de la modale critères si extraction non validée
  useEffect(() => {
    if (!project.criteriaValidated && project.criteria.length > 0) {
      // Pas d'ouverture auto intempestive : on ne l'ouvre que depuis le bandeau.
      // On laisse l'utilisateur choisir quand l'ouvrir.
    }
  }, [project.criteriaValidated, project.criteria.length]);

  // Sélection candidat + critère courants
  const [activeCandidateId, setActiveCandidateId] = useState<string | null>(
    project.candidates[0]?.id ?? null
  );
  const [activeCriterionId, setActiveCriterionId] = useState<string | null>(
    activeLeafCriteria[0]?.id ?? null
  );
  // Mode d'affichage : un critère à la fois (défaut) OU vue "Tout" qui agrège
  // toutes les citations du candidat courant, tous critères confondus.
  const [viewMode, setViewMode] = useState<'criterion' | 'all'>('criterion');

  // Maintien de la cohérence si le projet change
  useEffect(() => {
    if (project.candidates.length === 0) {
      setActiveCandidateId(null);
    } else if (!activeCandidateId || !project.candidates.some(c => c.id === activeCandidateId)) {
      setActiveCandidateId(project.candidates[0].id);
    }
  }, [project.candidates, activeCandidateId]);

  useEffect(() => {
    if (activeLeafCriteria.length === 0) {
      setActiveCriterionId(null);
    } else if (
      !activeCriterionId ||
      !activeLeafCriteria.some(c => c.id === activeCriterionId)
    ) {
      setActiveCriterionId(activeLeafCriteria[0].id);
    }
  }, [activeLeafCriteria, activeCriterionId]);

  // Click-outside pour ⋮
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuOpen]);

  // Sélection citation (sync entre PDF et éditeur)
  const [activeCitationId, setActiveCitationId] = useState<string | null>(null);
  const [hoveredCitationId, setHoveredCitationId] = useState<string | null>(null);

  const activeCandidate = useMemo(
    () => project.candidates.find(c => c.id === activeCandidateId) ?? null,
    [project.candidates, activeCandidateId]
  );
  const activeCriterion = useMemo(
    () => activeLeafCriteria.find(c => c.id === activeCriterionId) ?? null,
    [activeLeafCriteria, activeCriterionId]
  );
  const activeEvaluation = useMemo(() => {
    if (!activeCandidate || !activeCriterion) return undefined;
    return project.evaluations.find(
      e => e.candidateId === activeCandidate.id && e.criterionId === activeCriterion.id
    );
  }, [project.evaluations, activeCandidate, activeCriterion]);

  const candidateFiles = useMemo(
    () =>
      activeCandidate
        ? project.files.filter(f => f.candidateId === activeCandidate.id)
        : [],
    [project.files, activeCandidate]
  );

  const cellCitations = useMemo(() => {
    if (!activeEvaluation) return [];
    return project.citations.filter(c => c.evaluationId === activeEvaluation.id);
  }, [project.citations, activeEvaluation]);

  // ── Mode "Tout" : scope candidat × tous critères actifs feuilles ──
  //
  // Assignation stable d'une couleur par critère (ordre de code croissant).
  // Construit toujours — même en mode "criterion" — pour que la pastille
  // de légende et le AllCitationsPanel partagent la même source.
  const criterionColorMap = useMemo(
    () => buildCriterionColorMap(activeLeafCriteria),
    [activeLeafCriteria]
  );

  // IDs d'évaluations du candidat courant pour les critères actifs feuilles
  // (on exclut volontairement les critères inactifs).
  const candidateEvaluationIds = useMemo(() => {
    if (!activeCandidate) return new Set<string>();
    const activeLeafIdSet = new Set(activeLeafCriteria.map(c => c.id));
    return new Set(
      project.evaluations
        .filter(e => e.candidateId === activeCandidate.id && activeLeafIdSet.has(e.criterionId))
        .map(e => e.id)
    );
  }, [project.evaluations, activeCandidate, activeLeafCriteria]);

  const candidateCitations = useMemo(
    () => project.citations.filter(c => candidateEvaluationIds.has(c.evaluationId)),
    [project.citations, candidateEvaluationIds]
  );

  // Map citation → méta (critère + couleur). Utilisé par MockPdfViewer en
  // mode "Tout" et par AllCitationsPanel pour colorer les groupes.
  const citationMeta = useMemo(() => {
    const map = new Map<string, CitationMeta>();
    const evalById = new Map(project.evaluations.map(e => [e.id, e]));
    const critById = new Map(activeLeafCriteria.map(c => [c.id, c]));
    candidateCitations.forEach(cit => {
      const ev = evalById.get(cit.evaluationId);
      if (!ev) return;
      const crit = critById.get(ev.criterionId);
      if (!crit) return;
      const color = criterionColorMap.get(crit.id);
      if (!color) return;
      map.set(cit.id, {
        criterionId: crit.id,
        code: crit.code,
        title: crit.title,
        color,
      });
    });
    return map;
  }, [candidateCitations, project.evaluations, activeLeafCriteria, criterionColorMap]);

  // ── Undo toast pour la validation en masse ─────────────────────
  // On stocke uniquement les IDs qui étaient encore `!verified` au moment
  // du clic : on sait exactement ce qu'il faut re-basculer à `false` en
  // cas d'annulation (on n'affecte pas les citations déjà vérifiées).
  type UndoSnapshot = { citationIds: string[]; expiresAt: number };
  const [undoSnapshot, setUndoSnapshot] = useState<UndoSnapshot | null>(null);

  useEffect(() => {
    if (!undoSnapshot) return;
    const ms = undoSnapshot.expiresAt - Date.now();
    if (ms <= 0) {
      setUndoSnapshot(null);
      return;
    }
    const t = window.setTimeout(() => setUndoSnapshot(null), ms);
    return () => window.clearTimeout(t);
  }, [undoSnapshot]);

  // Changer de candidat invalide le snapshot (on ne peut plus annuler une
  // action qui concernait un autre candidat).
  useEffect(() => {
    setUndoSnapshot(null);
  }, [activeCandidateId]);

  const handleBulkValidateAi = useCallback(
    (citationIds: string[]) => {
      if (citationIds.length === 0) return;
      onBulkVerifyCitations(citationIds, true);
      setUndoSnapshot({
        citationIds,
        expiresAt: Date.now() + 6000,
      });
    },
    [onBulkVerifyCitations]
  );

  const handleUndoBulkValidate = useCallback(() => {
    if (!undoSnapshot) return;
    onBulkVerifyCitations(undoSnapshot.citationIds, false);
    setUndoSnapshot(null);
  }, [onBulkVerifyCitations, undoSnapshot]);

  // ── Handlers évaluation courante ─────────────────────────────
  const handleSaveEvaluation = (patch: Partial<Evaluation>) => {
    if (!activeCandidate || !activeCriterion) return;
    onUpdateEvaluation(activeCandidate.id, activeCriterion.id, patch);
  };

  const handleAddCitation = (text: string, sourceFileId: string, sourcePage: number) => {
    if (!activeEvaluation) {
      // Créer d'abord une évaluation vide pour attacher la citation
      if (!activeCandidate || !activeCriterion) return;
      onUpdateEvaluation(activeCandidate.id, activeCriterion.id, {});
      // L'ID est déterministe : `e-${candidateId}-${criterionId}` (voir AnalyseAO)
      const newEvalId = `e-${activeCandidate.id}-${activeCriterion.id}`;
      onAddCitation(newEvalId, text, sourceFileId, sourcePage);
      return;
    }
    onAddCitation(activeEvaluation.id, text, sourceFileId, sourcePage);
  };

  // Raccourcis V/R sur la citation active
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!activeCitationId) return;
      if (!project.criteriaValidated) return;
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      const cit = project.citations.find(c => c.id === activeCitationId);
      if (!cit) return;
      if (e.key === 'v' || e.key === 'V') {
        e.preventDefault();
        onVerifyCitation(cit.id, !cit.verified);
      } else if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        onRemoveCitation(cit.id);
        setActiveCitationId(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeCitationId, project.citations, project.criteriaValidated, onVerifyCitation, onRemoveCitation]);

  // Raccourcis [ / ] pour passer au candidat précédent/suivant
  // (pratique pour "je note ce critère pour tout le monde d'affilée")
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (project.candidates.length === 0) return;
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (e.key !== '[' && e.key !== ']') return;
      const idx = project.candidates.findIndex(c => c.id === activeCandidateId);
      if (idx < 0) return;
      e.preventDefault();
      if (e.key === '[' && idx > 0) {
        setActiveCandidateId(project.candidates[idx - 1].id);
      } else if (e.key === ']' && idx < project.candidates.length - 1) {
        setActiveCandidateId(project.candidates[idx + 1].id);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [project.candidates, activeCandidateId]);

  // ── Rendu ────────────────────────────────────────────────────
  const hasRegulation = stats.hasRegulation;
  const hasCriteria = project.criteria.length > 0;
  const hasCandidates = project.candidates.length > 0;

  const showExtractionBanner = !project.criteriaValidated && hasCriteria;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* ── Header projet ────────────────────────────────── */}
      <header className="shrink-0 bg-white border-b border-gray-200">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="shrink-0 p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            aria-label="Retour à la liste des projets"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0 flex items-center gap-2.5 flex-wrap">
            <h1 className="text-lg font-semibold text-gray-900 truncate">
              {project.name}
            </h1>
            {project.reference && (
              <span className="text-xs font-medium text-gray-500 tabular-nums">
                · {project.reference}
              </span>
            )}
            <StatusBadge status={project.status} />
          </div>
          <div className="shrink-0 flex items-center gap-1.5">
            <ActionChip
              icon={<DocumentTextIcon className="w-4 h-4" />}
              label={hasRegulation ? `RC (${stats.regulationFileCount})` : 'RC à déposer'}
              tone={hasRegulation ? 'neutral' : 'amber'}
              onClick={() => setRcOverlayOpen(true)}
            />
            <ActionChip
              icon={<ListBulletIcon className="w-4 h-4" />}
              label={
                hasCriteria
                  ? `Critères ${stats.activeLeafCriteriaCount}/${stats.criteriaCount}`
                  : 'Critères'
              }
              tone={showExtractionBanner ? 'amber' : 'neutral'}
              onClick={() => setCriteriaOverlayOpen(true)}
              disabled={!hasCriteria && !hasRegulation}
            />
            <ActionChip
              icon={<ChartBarIcon className="w-4 h-4" />}
              label="Classement"
              tone="neutral"
              onClick={() => setRankingOverlayOpen(true)}
              disabled={stats.evaluationsDone === 0}
            />
            <button
              type="button"
              onClick={onExportRao}
              disabled={stats.progressPct < 100}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
              title={
                stats.progressPct < 100
                  ? 'Toutes les évaluations doivent être validées pour exporter le RAO.'
                  : 'Exporter le rapport d\'analyse'
              }
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              RAO
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  setMenuOpen(o => !o);
                }}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                aria-label="Plus d'actions"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <EllipsisVerticalIcon className="w-5 h-5" />
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  onClick={e => e.stopPropagation()}
                  className="absolute right-0 top-full mt-1 z-20 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onRunAiScoring();
                    }}
                    disabled={!project.criteriaValidated || !hasCandidates}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <SparklesIcon className="w-4 h-4 text-violet-600" />
                    Relancer la notation IA
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    disabled
                    className="w-full px-3 py-2 text-left text-sm text-gray-400 flex items-center gap-2 cursor-not-allowed"
                    title="Bientôt disponible"
                  >
                    <ArchiveBoxIcon className="w-4 h-4" />
                    Archiver le projet
                  </button>
                  <div className="my-1 border-t border-gray-100" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirmDelete(true);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Supprimer le projet
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bandeau extraction (conditionnel) */}
        {showExtractionBanner && (
          <div className="bg-amber-50 border-t border-amber-200">
            <div className="max-w-[1600px] mx-auto px-4 py-2.5 flex items-center gap-3">
              <ExclamationTriangleIcon className="shrink-0 w-4 h-4 text-amber-700" />
              <div className="flex-1 min-w-0 text-sm text-amber-900">
                <span className="font-medium">Critères extraits du règlement.</span>{' '}
                <span>Relisez-les et validez-les avant de commencer la notation.</span>
              </div>
              <button
                type="button"
                onClick={() => setCriteriaOverlayOpen(true)}
                className="shrink-0 inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-amber-900 border border-amber-300 bg-white rounded-lg hover:bg-amber-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              >
                Ouvrir les critères
                <ArrowRightIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ── Rails candidats / critères ───────────────────── */}
      {hasCandidates && (
        <div className="shrink-0 bg-white border-b border-gray-200">
          {/* Rail candidats — hybride intelligent :
              • ≤ 5 : tabs horizontaux (vue d'ensemble immédiate)
              • > 5 : selector compact + flèches ◀ ▶ + dropdown recherche
              Note : on ne met pas `overflow-x-auto` sur le conteneur extérieur
              pour ne pas rogner le dropdown du selector. L'overflow n'est
              appliqué qu'à la liste des tabs, là où on en a vraiment besoin. */}
          <div className="max-w-[1600px] mx-auto px-4 py-2 flex items-center gap-2">
            <span className="shrink-0 text-[10px] font-medium text-gray-500 uppercase tracking-wide w-20">
              Candidats
            </span>
            {project.candidates.length <= 5 ? (
              <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto">
                {project.candidates.map(cand => (
                  <CandidateTab
                    key={cand.id}
                    candidate={cand}
                    active={cand.id === activeCandidateId}
                    criteriaCount={activeLeafCriteria.length}
                    evaluations={project.evaluations.filter(e => e.candidateId === cand.id)}
                    onClick={() => setActiveCandidateId(cand.id)}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setCandidateModalOpen(true)}
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-violet-700 border border-dashed border-violet-300 rounded-lg hover:bg-violet-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                  title="Ajouter un candidat"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Candidat
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <CandidateSelector
                  candidates={project.candidates}
                  activeId={activeCandidateId}
                  criteriaCount={activeLeafCriteria.length}
                  evaluations={project.evaluations}
                  onSelect={setActiveCandidateId}
                />
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={() => setCandidateModalOpen(true)}
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-violet-700 border border-dashed border-violet-300 rounded-lg hover:bg-violet-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                  title="Ajouter un candidat"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Candidat
                </button>
              </div>
            )}
          </div>

          {/* Rail critères */}
          {activeLeafCriteria.length > 0 && (
            <div className="max-w-[1600px] mx-auto px-4 py-2 flex items-center gap-2 border-t border-gray-100 overflow-x-auto">
              <span className="shrink-0 text-[10px] font-medium text-gray-500 uppercase tracking-wide w-20">
                Critères
              </span>
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <AllCriteriaChip
                  active={viewMode === 'all'}
                  citationCount={candidateCitations.length}
                  onClick={() => setViewMode(viewMode === 'all' ? 'criterion' : 'all')}
                />
                <span className="shrink-0 h-4 w-px bg-gray-200 mx-0.5" aria-hidden />
                {activeLeafCriteria.map(crit => {
                  const evalCell = activeCandidate
                    ? project.evaluations.find(
                        e => e.candidateId === activeCandidate.id && e.criterionId === crit.id
                      )
                    : undefined;
                  const scored = evalCell?.userScore !== undefined;
                  const hasAi = evalCell?.aiScore !== undefined;
                  return (
                    <CriterionChip
                      key={crit.id}
                      criterion={crit}
                      color={criterionColorMap.get(crit.id)}
                      active={viewMode === 'criterion' && crit.id === activeCriterionId}
                      dimmed={viewMode === 'all'}
                      scored={scored}
                      hasAi={hasAi}
                      onClick={() => {
                        setActiveCriterionId(crit.id);
                        setViewMode('criterion');
                      }}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Zone centrale ───────────────────────────────── */}
      <main className="flex-1 min-h-0">
        {!hasRegulation ? (
          <CenterEmptyState
            icon={<DocumentTextIcon className="w-12 h-12" />}
            title="Commencez par déposer le règlement de consultation"
            description="L'IA s'en servira pour extraire automatiquement les critères d'évaluation."
            cta={{
              label: 'Déposer le règlement',
              onClick: () => setRcOverlayOpen(true),
            }}
          />
        ) : !hasCriteria ? (
          <CenterEmptyState
            icon={<SparklesIcon className="w-12 h-12" />}
            title="Extraire les critères depuis le règlement"
            description="Lancez l'extraction IA pour obtenir une première grille que vous pourrez ajuster."
            cta={{
              label: "Lancer l'extraction",
              onClick: () => {
                onReextractCriteria();
                setCriteriaOverlayOpen(true);
              },
            }}
          />
        ) : !hasCandidates ? (
          <CenterEmptyState
            icon={<UserGroupIcon className="w-12 h-12" />}
            title="Ajoutez un premier candidat"
            description="Importez le mémoire technique d'un candidat pour lancer les évaluations."
            cta={{
              label: 'Ajouter un candidat',
              onClick: () => setCandidateModalOpen(true),
            }}
          />
        ) : (
          <div className="h-full grid grid-cols-[minmax(0,3fr)_minmax(380px,2fr)] gap-px bg-gray-200">
            {/* PDF viewer gauche */}
            <div className="bg-white min-h-0 h-[calc(100vh-220px)]">
              <MockPdfViewer
                candidate={activeCandidate}
                files={candidateFiles}
                citations={viewMode === 'all' ? candidateCitations : cellCitations}
                activeCitationId={activeCitationId}
                hoveredCitationId={hoveredCitationId}
                onSelectCitation={setActiveCitationId}
                onHoverCitation={setHoveredCitationId}
                mode={viewMode}
                citationMeta={viewMode === 'all' ? citationMeta : undefined}
                legendCriteria={
                  viewMode === 'all'
                    ? activeLeafCriteria.map(c => ({
                        id: c.id,
                        code: c.code,
                        title: c.title,
                        color: criterionColorMap.get(c.id)!,
                      }))
                    : undefined
                }
              />
            </div>
            {/* Panneau droite : éditeur (critère unique) ou liste groupée (mode Tout) */}
            <div className="bg-white min-h-0 h-[calc(100vh-220px)]">
              {viewMode === 'all' ? (
                <AllCitationsPanel
                  candidate={activeCandidate}
                  criteria={activeLeafCriteria}
                  evaluations={project.evaluations}
                  citations={candidateCitations}
                  files={project.files}
                  colorMap={criterionColorMap}
                  criteriaValidated={project.criteriaValidated}
                  activeCitationId={activeCitationId}
                  hoveredCitationId={hoveredCitationId}
                  onSelectCitation={setActiveCitationId}
                  onHoverCitation={setHoveredCitationId}
                  onVerifyCitation={onVerifyCitation}
                  onRemoveCitation={onRemoveCitation}
                  onBulkValidateAi={handleBulkValidateAi}
                  onFocusCriterion={critId => {
                    setActiveCriterionId(critId);
                    setViewMode('criterion');
                  }}
                />
              ) : (
                <EvaluationEditor
                  candidate={activeCandidate}
                  criterion={activeCriterion}
                  evaluation={activeEvaluation}
                  citations={project.citations}
                  files={project.files}
                  criteriaValidated={project.criteriaValidated}
                  referenceNow={REFERENCE_NOW_ISO}
                  activeCitationId={activeCitationId}
                  hoveredCitationId={hoveredCitationId}
                  onSelectCitation={setActiveCitationId}
                  onHoverCitation={setHoveredCitationId}
                  onSaveEvaluation={handleSaveEvaluation}
                  onVerifyCitation={onVerifyCitation}
                  onRemoveCitation={onRemoveCitation}
                  onAddCitation={handleAddCitation}
                  onOpenCriteria={() => setCriteriaOverlayOpen(true)}
                />
              )}
            </div>
          </div>
        )}
      </main>

      {/* ── Toast d'annulation (après validation en masse) ────── */}
      {undoSnapshot && (
        <UndoToast
          count={undoSnapshot.citationIds.length}
          onUndo={handleUndoBulkValidate}
          onDismiss={() => setUndoSnapshot(null)}
        />
      )}

      {/* ── Overlays ────────────────────────────────────── */}
      <CriteriaOverlay
        open={criteriaOverlayOpen}
        criteria={project.criteria}
        validated={project.criteriaValidated}
        onClose={() => setCriteriaOverlayOpen(false)}
        onCreateCriterion={onCreateCriterion}
        onUpdateCriterion={onUpdateCriterion}
        onDeleteCriterion={onDeleteCriterion}
        onToggleActive={onToggleCriterionActive}
        onReextract={onReextractCriteria}
        onValidate={() => {
          onValidateCriteria();
          setCriteriaOverlayOpen(false);
        }}
      />

      {rcOverlayOpen && (
        <RegulationOverlay
          files={project.files.filter(f => f.source === 'regulation')}
          onClose={() => setRcOverlayOpen(false)}
          onAddFiles={onAddRegulationFiles}
          onRemoveFile={onRemoveFile}
        />
      )}

      {candidateModalOpen && (
        <NewCandidateModal
          onClose={() => setCandidateModalOpen(false)}
          onCreate={name => {
            onAddCandidate(name);
            setCandidateModalOpen(false);
          }}
        />
      )}

      {/* Drop zone pour le candidat actif s'il n'a pas encore de fichier — affichée en overlay léger */}
      {activeCandidate && candidateFiles.length === 0 && hasCandidates && (
        <CandidateFilesPrompt
          candidate={activeCandidate}
          onAdd={files => onAddCandidateFiles(activeCandidate.id, files)}
        />
      )}

      {rankingOverlayOpen && (
        <RankingOverlay
          project={project}
          onClose={() => setRankingOverlayOpen(false)}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          project={project}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            setConfirmDelete(false);
            onDelete();
          }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Sous-composants
// ─────────────────────────────────────────────────────────────────

interface ActionChipProps {
  icon: React.ReactNode;
  label: string;
  tone: 'neutral' | 'amber';
  onClick: () => void;
  disabled?: boolean;
}

const ActionChip: React.FC<ActionChipProps> = ({ icon, label, tone, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={
      tone === 'amber'
        ? 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 disabled:opacity-40 disabled:cursor-not-allowed'
        : 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 disabled:opacity-40 disabled:cursor-not-allowed'
    }
  >
    {icon}
    {label}
  </button>
);

// ─── Selector candidat (mode > 5) ─────────────────────────────────

interface CandidateSelectorProps {
  candidates: Candidate[];
  activeId: string | null;
  criteriaCount: number;
  evaluations: Evaluation[];
  onSelect: (id: string) => void;
}

/**
 * Selector compact pour la navigation entre candidats quand il y en a
 * beaucoup (> 5). Offre :
 *  - flèches ◀ ▶ pour avancer/reculer (raccourcis [ / ])
 *  - un bouton principal : dot statut + nom + position X/N
 *  - un dropdown avec recherche plein texte + avancement par candidat
 */
const CandidateSelector: React.FC<CandidateSelectorProps> = ({
  candidates,
  activeId,
  criteriaCount,
  evaluations,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const activeIdx = candidates.findIndex(c => c.id === activeId);
  const active = activeIdx >= 0 ? candidates[activeIdx] : null;

  // Stats pour la pastille du bouton principal
  const activeStats = useMemo(() => {
    if (!active) return { pct: 0, hasFiles: false };
    const done = evaluations.filter(
      e => e.candidateId === active.id && e.userScore !== undefined
    ).length;
    return {
      pct: criteriaCount > 0 ? Math.round((done / criteriaCount) * 100) : 0,
      hasFiles: active.fileIds.length > 0,
    };
  }, [active, evaluations, criteriaCount]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(c => c.name.toLowerCase().includes(q));
  }, [candidates, search]);

  // Click-outside + auto-focus recherche
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    window.addEventListener('mousedown', handler);
    requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const handlePrev = () => {
    if (activeIdx > 0) onSelect(candidates[activeIdx - 1].id);
  };
  const handleNext = () => {
    if (activeIdx < candidates.length - 1) onSelect(candidates[activeIdx + 1].id);
  };

  return (
    <div ref={rootRef} className="shrink-0 flex items-center gap-1">
      <button
        type="button"
        onClick={handlePrev}
        disabled={activeIdx <= 0}
        className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
        aria-label="Candidat précédent"
        title="Candidat précédent  ["
      >
        <ChevronLeftIcon className="w-4 h-4" />
      </button>

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-2 px-3 py-1.5 min-w-[260px] rounded-lg border border-gray-200 bg-white text-gray-900 text-sm font-medium hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span
            className={`shrink-0 w-2 h-2 rounded-full ${
              activeStats.hasFiles
                ? activeStats.pct === 100
                  ? 'bg-emerald-500'
                  : 'bg-violet-500'
                : 'bg-gray-300'
            }`}
            aria-hidden
          />
          <span className="flex-1 truncate text-left">{active?.name ?? '—'}</span>
          {activeStats.hasFiles && criteriaCount > 0 && (
            <span className="shrink-0 text-[10px] tabular-nums text-gray-500">
              {activeStats.pct}%
            </span>
          )}
          <span className="shrink-0 text-[10px] tabular-nums text-gray-400 border-l border-gray-200 pl-2">
            {activeIdx + 1}/{candidates.length}
          </span>
          <ChevronDownIcon className="shrink-0 w-4 h-4 text-gray-500" />
        </button>

        {open && (
          <div
            role="dialog"
            className="absolute top-full left-0 mt-1 z-30 w-[360px] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          >
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Rechercher un candidat…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500"
                />
              </div>
            </div>
            <ul className="max-h-80 overflow-y-auto py-1" role="listbox">
              {filtered.length === 0 ? (
                <li className="px-3 py-6 text-sm text-gray-500 text-center">
                  Aucun candidat ne correspond.
                </li>
              ) : (
                filtered.map(cand => {
                  const done = evaluations.filter(
                    e => e.candidateId === cand.id && e.userScore !== undefined
                  ).length;
                  const pct =
                    criteriaCount > 0 ? Math.round((done / criteriaCount) * 100) : 0;
                  const hasFiles = cand.fileIds.length > 0;
                  const isActive = cand.id === activeId;
                  return (
                    <li key={cand.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onClick={() => {
                          onSelect(cand.id);
                          setOpen(false);
                          setSearch('');
                        }}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                          isActive
                            ? 'bg-violet-50 text-violet-800'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span
                          className={`shrink-0 w-2 h-2 rounded-full ${
                            hasFiles
                              ? pct === 100
                                ? 'bg-emerald-500'
                                : 'bg-violet-500'
                              : 'bg-gray-300'
                          }`}
                          aria-hidden
                        />
                        <span className="flex-1 truncate">{cand.name}</span>
                        {hasFiles && criteriaCount > 0 && (
                          <span className="shrink-0 text-[10px] tabular-nums text-gray-500">
                            {pct}%
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 text-[11px] text-gray-500 flex items-center gap-3">
              <span>
                <kbd className="px-1 py-0.5 border border-gray-300 rounded text-gray-600 bg-white text-[10px]">[</kbd>{' '}
                /{' '}
                <kbd className="px-1 py-0.5 border border-gray-300 rounded text-gray-600 bg-white text-[10px]">]</kbd>{' '}
                naviguer
              </span>
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={handleNext}
        disabled={activeIdx >= candidates.length - 1}
        className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
        aria-label="Candidat suivant"
        title="Candidat suivant  ]"
      >
        <ChevronRightIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

// ─── Tab candidat ─────────────────────────────────────────────────

interface CandidateTabProps {
  candidate: Candidate;
  active: boolean;
  criteriaCount: number;
  evaluations: Evaluation[];
  onClick: () => void;
}

const CandidateTab: React.FC<CandidateTabProps> = ({
  candidate,
  active,
  criteriaCount,
  evaluations,
  onClick,
}) => {
  const done = evaluations.filter(e => e.userScore !== undefined).length;
  const pct = criteriaCount > 0 ? Math.round((done / criteriaCount) * 100) : 0;
  const hasFiles = candidate.fileIds.length > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-violet-300 bg-violet-50 text-violet-800 text-sm font-medium transition-colors'
          : 'shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-transparent bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 hover:border-gray-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500'
      }
      aria-pressed={active}
    >
      <span
        className={`w-2 h-2 rounded-full ${
          hasFiles ? (pct === 100 ? 'bg-emerald-500' : 'bg-violet-500') : 'bg-gray-300'
        }`}
        aria-hidden
        title={hasFiles ? (pct === 100 ? 'Complet' : 'En cours') : 'Sans fichier'}
      />
      <span className="whitespace-nowrap">{candidate.name}</span>
      {hasFiles && criteriaCount > 0 && (
        <span
          className={`text-[10px] tabular-nums ${
            active ? 'text-violet-700' : 'text-gray-500'
          }`}
        >
          {pct}%
        </span>
      )}
    </button>
  );
};

// ─── Chip "Tout" (mode agrégé) ────────────────────────────────────

interface AllCriteriaChipProps {
  active: boolean;
  citationCount: number;
  onClick: () => void;
}

const AllCriteriaChip: React.FC<AllCriteriaChipProps> = ({ active, citationCount, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title="Voir toutes les citations du candidat (tous critères)"
    aria-pressed={active}
    className={
      active
        ? 'shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-violet-500 bg-violet-600 text-white text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2'
        : 'shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-violet-200 bg-white text-violet-700 text-xs font-semibold hover:bg-violet-50 hover:border-violet-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500'
    }
  >
    <Squares2X2Icon className="w-3.5 h-3.5" />
    <span>Tout</span>
    {citationCount > 0 && (
      <span
        className={
          active
            ? 'tabular-nums text-[10px] font-semibold px-1 rounded bg-white/20'
            : 'tabular-nums text-[10px] font-semibold px-1 rounded bg-violet-100 text-violet-700'
        }
      >
        {citationCount}
      </span>
    )}
  </button>
);

// ─── Chip critère ─────────────────────────────────────────────────

interface CriterionChipProps {
  criterion: Criterion;
  color?: CriterionColor;
  active: boolean;
  dimmed?: boolean;
  scored: boolean;
  hasAi: boolean;
  onClick: () => void;
}

/**
 * Chip critère — volontairement compact : on n'affiche que le code + une
 * pastille de statut. Le titre complet apparaît :
 *  - au survol via `title` (tooltip natif)
 *  - en permanence dans le header de l'éditeur à droite
 * Ça permet d'aligner 15+ critères sans scroll horizontal.
 *
 * `dimmed` : en mode "Tout", les chips restent cliquables (retour au focus
 * critère) mais sont visuellement atténués pour bien marquer que c'est le
 * chip "Tout" qui est actif.
 * `color` : pastille de la palette critère (sert aussi de légende implicite).
 */
const CriterionChip: React.FC<CriterionChipProps> = ({
  criterion,
  color,
  active,
  dimmed,
  scored,
  hasAi,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    title={`${criterion.code} · ${criterion.title}`}
    aria-label={`${criterion.code} ${criterion.title}`}
    className={
      active
        ? 'shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-violet-300 bg-violet-50 text-violet-800 text-xs font-medium transition-colors'
        : dimmed
        ? 'shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-gray-200 bg-white text-gray-500 text-xs font-medium opacity-70 hover:opacity-100 hover:bg-gray-50 hover:border-gray-300 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500'
        : 'shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-gray-200 bg-white text-gray-700 text-xs font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500'
    }
    aria-pressed={active}
  >
    {/* Pastille : couleur critère prioritaire en mode dimmed (valeur de légende), statut sinon */}
    {dimmed && color ? (
      <span className={`shrink-0 w-3.5 h-3.5 rounded-full ${color.dot}`} aria-hidden />
    ) : (
      <span
        className={`shrink-0 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full ${
          scored
            ? 'bg-emerald-500 text-white'
            : hasAi
            ? 'bg-amber-400 text-white'
            : 'bg-gray-200 text-gray-400'
        }`}
        aria-hidden
      >
        {scored ? (
          <CheckCircleIcon className="w-3 h-3" />
        ) : hasAi ? (
          <SparklesIcon className="w-2.5 h-2.5" />
        ) : null}
      </span>
    )}
    <span className="tabular-nums text-xs">{criterion.code}</span>
  </button>
);

// ─── Toast d'annulation (validation en masse) ─────────────────────

interface UndoToastProps {
  count: number;
  onUndo: () => void;
  onDismiss: () => void;
}

const UndoToast: React.FC<UndoToastProps> = ({ count, onUndo, onDismiss }) => (
  <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 bg-gray-900 text-white rounded-lg shadow-lg">
    <CheckCircleIcon className="shrink-0 w-4 h-4 text-emerald-400" />
    <span className="text-sm tabular-nums">
      {count} citation{count > 1 ? 's' : ''} validée{count > 1 ? 's' : ''}
    </span>
    <button
      type="button"
      onClick={onUndo}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-violet-300 hover:text-violet-100 hover:bg-white/10 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
    >
      <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
      Annuler
    </button>
    <button
      type="button"
      onClick={onDismiss}
      className="p-1 rounded hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
      aria-label="Fermer"
    >
      <XMarkIcon className="w-3.5 h-3.5" />
    </button>
  </div>
);

// ─── Empty state central ──────────────────────────────────────────

interface CenterEmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta: { label: string; onClick: () => void };
}

const CenterEmptyState: React.FC<CenterEmptyStateProps> = ({ icon, title, description, cta }) => (
  <div className="h-full flex items-center justify-center p-8 bg-gray-50">
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10 text-center max-w-md">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-violet-50 text-violet-700 mb-4">
        {icon}
      </div>
      <h2 className="text-lg font-semibold text-gray-900 mb-2">{title}</h2>
      <p className="text-sm text-gray-600 mb-6 leading-relaxed">{description}</p>
      <button
        type="button"
        onClick={cta.onClick}
        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
      >
        <PlusIcon className="w-4 h-4" />
        {cta.label}
      </button>
    </div>
  </div>
);

// ─── Overlay : Règlement de consultation ─────────────────────────

interface RegulationOverlayProps {
  files: UploadedFile[];
  onClose: () => void;
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (fileId: string) => void;
}

const RegulationOverlay: React.FC<RegulationOverlayProps> = ({
  files,
  onClose,
  onAddFiles,
  onRemoveFile,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (list: FileList | File[] | null) => {
    if (!list) return;
    const arr = Array.from(list).filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    if (arr.length > 0) onAddFiles(arr);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rc-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <header className="shrink-0 px-6 py-4 border-b border-gray-200 flex items-start justify-between">
          <div>
            <h2 id="rc-title" className="text-lg font-semibold text-gray-900">
              Règlement de consultation
            </h2>
            <p className="text-sm text-gray-600 mt-0.5">
              RC, CCTP, annexes — le pack de documents qui cadre l'évaluation.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            aria-label="Fermer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Dropzone */}
          <div
            onDragOver={e => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => {
              e.preventDefault();
              setDragOver(false);
              handleFiles(e.dataTransfer.files);
            }}
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
              dragOver ? 'border-violet-400 bg-violet-50' : 'border-gray-300 bg-gray-50'
            }`}
          >
            <CloudArrowUpIcon className="mx-auto w-10 h-10 text-gray-400 mb-2" />
            <p className="text-sm text-gray-700 mb-2">
              Glissez-déposez vos fichiers PDF ici
            </p>
            <p className="text-xs text-gray-500 mb-3">ou</p>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              multiple
              className="hidden"
              onChange={e => {
                handleFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-violet-700 border border-violet-200 bg-white rounded-lg hover:bg-violet-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            >
              <PlusIcon className="w-4 h-4" />
              Parcourir…
            </button>
          </div>

          {/* Liste */}
          {files.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              Aucun fichier de règlement déposé pour le moment.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
              {files.map(f => (
                <li key={f.id} className="flex items-center gap-3 p-3">
                  <DocumentTextIcon className="shrink-0 w-5 h-5 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 truncate" title={f.name}>
                      {f.name}
                    </p>
                    <p className="text-xs text-gray-500 tabular-nums">
                      {formatFileSize(f.sizeBytes)}
                      {f.pages && ` · ${f.pages} p.`}
                      {` · déposé ${formatRelative(f.uploadedAt, REFERENCE_NOW_ISO)}`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveFile(f.id)}
                    className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                    aria-label={`Retirer ${f.name}`}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="shrink-0 px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
          >
            Fermer
          </button>
        </footer>
      </div>
    </div>
  );
};

// ─── Modale : Nouveau candidat ───────────────────────────────────

interface NewCandidateModalProps {
  onClose: () => void;
  onCreate: (name: string) => void;
}

const NewCandidateModal: React.FC<NewCandidateModalProps> = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Nouveau candidat</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            aria-label="Fermer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form
          onSubmit={e => {
            e.preventDefault();
            if (!name.trim()) return;
            onCreate(name.trim());
          }}
          className="space-y-4"
        >
          <div>
            <label htmlFor="cand-name" className="block text-xs font-medium text-gray-700 mb-1">
              Nom de l'entreprise
            </label>
            <input
              id="cand-name"
              type="text"
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex : Bouygues Énergies & Services"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500"
            />
          </div>
          <p className="text-xs text-gray-500">
            Vous pourrez glisser le mémoire technique juste après la création.
          </p>
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            >
              Créer le candidat
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Prompt : dépôt du mémoire pour le candidat actif ────────────

interface CandidateFilesPromptProps {
  candidate: Candidate;
  onAdd: (files: File[]) => void;
}

const CandidateFilesPrompt: React.FC<CandidateFilesPromptProps> = ({ candidate, onAdd }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 bg-white border border-violet-200 rounded-xl shadow-lg px-4 py-3 flex items-center gap-3 max-w-md"
      role="status"
    >
      <div className="shrink-0 p-1.5 rounded-lg bg-violet-50 text-violet-700">
        <CloudArrowUpIcon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">
          Aucun mémoire pour <span className="text-violet-700">{candidate.name}</span>
        </p>
        <p className="text-xs text-gray-500">Déposez-le pour lancer les évaluations.</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        multiple
        className="hidden"
        onChange={e => {
          const files = Array.from(e.target.files ?? []);
          if (files.length > 0) onAdd(files);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
      >
        <PlusIcon className="w-4 h-4" />
        Déposer
      </button>
    </div>
  );
};

// ─── Overlay : Classement (matrice critères × candidats) ────────

interface RankingOverlayProps {
  project: Project;
  onClose: () => void;
}

/**
 * Matrice critères (lignes) × candidats (colonnes, triés gauche→droite
 * de la meilleure à la pire note). Seule vue — c'est la plus riche et
 * permet à la fois la comparaison détaillée et le classement en un coup
 * d'œil via la ligne "Total" en bas.
 */
const RankingOverlay: React.FC<RankingOverlayProps> = ({ project, onClose }) => {
  const leafIds = useMemo(() => getLeafCriteriaIds(project.criteria), [project.criteria]);
  const activeLeaf = useMemo(
    () =>
      project.criteria
        .filter(c => leafIds.includes(c.id) && c.active)
        .sort((a, b) => a.code.localeCompare(b.code, 'fr', { numeric: true })),
    [project.criteria, leafIds]
  );

  const rows = useMemo(() => {
    return project.candidates.map(cand => {
      let sum = 0;
      let doneCount = 0;
      for (const crit of activeLeaf) {
        const ev = project.evaluations.find(
          e => e.candidateId === cand.id && e.criterionId === crit.id
        );
        const score = ev?.userScore ?? ev?.aiScore;
        if (score !== undefined) {
          sum += score;
          if (ev?.userScore !== undefined) doneCount += 1;
        }
      }
      return {
        candidate: cand,
        sum,
        doneCount,
        totalCount: activeLeaf.length,
      };
    });
  }, [project, activeLeaf]);

  // Toujours triés gauche→droite, meilleure → pire note.
  const sorted = useMemo(() => [...rows].sort((a, b) => b.sum - a.sum), [rows]);
  const sortedCandidates = useMemo(() => sorted.map(r => r.candidate), [sorted]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-[min(1400px,95vw)] max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <header className="shrink-0 px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Classement technique</h2>
            <p className="text-sm text-gray-600 mt-0.5">
              Matrice critères × candidats. Les offres sont classées de gauche à droite, de la meilleure à la pire note.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            aria-label="Fermer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-auto">
          {sorted.length === 0 ? (
            <div className="p-10 text-center text-sm text-gray-500">
              Aucun candidat à classer pour le moment.
            </div>
          ) : (
            <MatrixView
              project={project}
              criteria={activeLeaf}
              candidates={sortedCandidates}
              totals={sorted}
            />
          )}
        </div>

        <footer className="shrink-0 px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-4">
          <MatrixLegend />
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          >
            Fermer
          </button>
        </footer>
      </div>
    </div>
  );
};

// ─── Matrice — critères × candidats ───────────────────────────────

interface MatrixViewProps {
  project: Project;
  criteria: Criterion[];
  candidates: Candidate[];
  totals: Array<{ candidate: Candidate; sum: number; doneCount: number; totalCount: number }>;
}

/**
 * Matrice critères (lignes) × candidats (colonnes) avec :
 *  - colonne gauche et en-tête sticky pour faciliter la lecture avec scroll
 *  - cellules colorées selon la proportion score/poids du critère
 *  - italique + tooltip IA quand la note n'est pas validée par l'analyste
 *  - ligne de total en bas (sticky)
 */
const MatrixView: React.FC<MatrixViewProps> = ({ project, criteria, candidates, totals }) => {
  const totalsByCandidate = useMemo(() => {
    const map = new Map<string, { sum: number; doneCount: number; totalCount: number }>();
    totals.forEach(t => map.set(t.candidate.id, t));
    return map;
  }, [totals]);

  return (
    <table className="w-full text-sm border-collapse">
      <thead className="sticky top-0 z-20 bg-white">
        <tr className="border-b border-gray-200">
          <th className="sticky left-0 z-30 bg-white py-2.5 px-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wide min-w-[260px] border-r border-gray-200">
            Critère
          </th>
          <th className="bg-white py-2.5 px-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wide w-16 border-r border-gray-200">
            Pds
          </th>
          {candidates.map((cand, i) => (
            <th
              key={cand.id}
              className="bg-white py-2.5 px-2 text-center text-xs font-medium uppercase tracking-wide min-w-[120px] border-r border-gray-100 last:border-r-0"
            >
              <div className="flex items-center justify-center gap-1.5">
                <span
                  className={
                    i === 0
                      ? 'inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold tabular-nums'
                      : 'inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-gray-700 text-[10px] font-semibold tabular-nums'
                  }
                  aria-hidden
                >
                  {i + 1}
                </span>
                <span
                  className="text-gray-800 normal-case truncate max-w-[180px] text-[13px] font-medium"
                  title={cand.name}
                >
                  {cand.name}
                </span>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {criteria.map(crit => (
          <tr key={crit.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50">
            <td
              className="sticky left-0 z-10 bg-white py-2 px-3 border-r border-gray-200"
              title={crit.description ?? crit.title}
            >
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="shrink-0 text-[11px] text-gray-500 tabular-nums font-medium">
                  {crit.code}
                </span>
                <span className="text-sm text-gray-900 truncate">{crit.title}</span>
              </div>
            </td>
            <td className="py-2 px-2 text-right tabular-nums text-gray-500 text-xs border-r border-gray-200">
              {crit.weight}
            </td>
            {candidates.map(cand => {
              const ev = project.evaluations.find(
                e => e.candidateId === cand.id && e.criterionId === crit.id
              );
              const hasUser = ev?.userScore !== undefined;
              const score = ev?.userScore ?? ev?.aiScore;
              const isAiOnly = !hasUser && ev?.aiScore !== undefined;
              const tone = matrixCellTone(score, crit.weight, isAiOnly);
              return (
                <td
                  key={cand.id}
                  className={`py-1.5 px-2 text-center tabular-nums border-r border-gray-100 last:border-r-0 ${tone.bg} ${tone.text}`}
                  title={
                    score === undefined
                      ? 'Non évalué'
                      : isAiOnly
                      ? `Note IA (non validée) — ${score.toFixed(1)}/${crit.weight}`
                      : `Note validée par l'analyste — ${score.toFixed(1)}/${crit.weight}`
                  }
                >
                  {score === undefined ? (
                    <span className="text-gray-300">—</span>
                  ) : isAiOnly ? (
                    <span
                      className={`inline-flex items-center gap-1 italic text-[13px] px-1.5 py-0.5 rounded border border-dashed ${tone.badge ?? 'border-gray-300'}`}
                    >
                      <SparklesIcon className="w-3 h-3 opacity-70" />
                      {score.toFixed(1)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 font-semibold text-[13px]">
                      {score.toFixed(1)}
                    </span>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
      <tfoot className="sticky bottom-0 z-20 bg-gray-50 border-t-2 border-gray-300">
        <tr>
          <td className="sticky left-0 z-30 bg-gray-50 py-3 px-3 font-semibold text-gray-900 border-r border-gray-200">
            Total
          </td>
          <td className="bg-gray-50 py-3 px-2 text-right tabular-nums font-semibold text-gray-700 border-r border-gray-200">
            {criteria.reduce((acc, c) => acc + c.weight, 0)}
          </td>
          {candidates.map(cand => {
            const t = totalsByCandidate.get(cand.id);
            return (
              <td
                key={cand.id}
                className="bg-gray-50 py-3 px-2 text-center tabular-nums font-bold text-violet-700 border-r border-gray-100 last:border-r-0"
              >
                {t ? t.sum.toFixed(1) : '—'}
              </td>
            );
          })}
        </tr>
        <tr>
          <td className="sticky left-0 z-30 bg-gray-50 py-2 px-3 text-xs text-gray-500 border-r border-gray-200">
            Avancement
          </td>
          <td className="bg-gray-50 border-r border-gray-200" />
          {candidates.map(cand => {
            const t = totalsByCandidate.get(cand.id);
            const pct = t && t.totalCount > 0 ? Math.round((t.doneCount / t.totalCount) * 100) : 0;
            return (
              <td
                key={cand.id}
                className="bg-gray-50 py-2 px-2 text-center text-[11px] text-gray-500 tabular-nums border-r border-gray-100 last:border-r-0"
              >
                {t ? `${t.doneCount}/${t.totalCount} · ${pct}%` : '—'}
              </td>
            );
          })}
        </tr>
      </tfoot>
    </table>
  );
};

interface CellTone {
  bg: string;
  text: string;
  /** Couleur de bordure pour le pill dashed en mode IA. */
  badge?: string;
}

/**
 * Palette de la matrice.
 *
 * Objectif : distinguer fortement les notes **validées par l'analyste**
 * (décision) des notes **IA en attente** (provisoire).
 *
 *  • Validées → fond saturé (bg-*-100) + texte gras sombre → visuel "bloc plein"
 *  • IA       → fond très pâle + texte italique dans un pill à bordure
 *               pointillée avec icône ✶ → visuel "en suspens"
 *
 * Dans les deux cas, la couleur reste indexée sur le ratio score/poids
 * pour conserver la lecture à la volée.
 */
function matrixCellTone(
  score: number | undefined,
  weight: number,
  isAiOnly: boolean
): CellTone {
  if (score === undefined) return { bg: 'bg-white', text: 'text-gray-300' };
  const ratio = weight > 0 ? score / weight : 0;

  if (isAiOnly) {
    if (ratio >= 0.8) return { bg: 'bg-emerald-50/60', text: 'text-emerald-700', badge: 'border-emerald-300' };
    if (ratio >= 0.5) return { bg: 'bg-gray-50',        text: 'text-gray-600',    badge: 'border-gray-300' };
    if (ratio >= 0.25) return { bg: 'bg-amber-50/60',   text: 'text-amber-700',   badge: 'border-amber-300' };
    return { bg: 'bg-red-50/60', text: 'text-red-700', badge: 'border-red-300' };
  }
  // Notes humaines — saturées et décisives
  if (ratio >= 0.8) return { bg: 'bg-emerald-100', text: 'text-emerald-900' };
  if (ratio >= 0.5) return { bg: 'bg-white',        text: 'text-gray-900' };
  if (ratio >= 0.25) return { bg: 'bg-amber-100',  text: 'text-amber-900' };
  return { bg: 'bg-red-100', text: 'text-red-900' };
}

const MatrixLegend: React.FC = () => (
  <div className="flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
    <span className="font-medium text-gray-600 uppercase tracking-wide text-[10px]">Humain</span>
    <LegendSwatch className="bg-emerald-100 text-emerald-900 font-semibold" label="≥ 80%" content="17" />
    <LegendSwatch className="bg-white text-gray-900 font-semibold border border-gray-200" label="50–79%" content="12" />
    <LegendSwatch className="bg-amber-100 text-amber-900 font-semibold" label="25–49%" content="6" />
    <LegendSwatch className="bg-red-100 text-red-900 font-semibold" label="< 25%" content="2" />
    <span className="text-gray-300">│</span>
    <span className="font-medium text-gray-600 uppercase tracking-wide text-[10px]">IA</span>
    <span
      className="inline-flex items-center gap-1 italic px-1.5 py-0.5 rounded border border-dashed border-gray-300 bg-gray-50 text-gray-600 text-[11px]"
      title="Note IA non validée"
    >
      <SparklesIcon className="w-2.5 h-2.5 opacity-70" />
      en attente
    </span>
  </div>
);

const LegendSwatch: React.FC<{ className: string; label: string; content?: string }> = ({
  className,
  label,
  content,
}) => (
  <span className="inline-flex items-center gap-1">
    <span
      className={`inline-flex items-center justify-center w-7 h-5 rounded text-[10px] tabular-nums ${className}`}
      aria-hidden
    >
      {content ?? ''}
    </span>
    {label}
  </span>
);

// ─── Modale : Confirm delete ─────────────────────────────────────

interface ConfirmDeleteModalProps {
  project: Project;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({ project, onCancel, onConfirm }) => (
  <div
    className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
    role="dialog"
    aria-modal="true"
    onClick={onCancel}
  >
    <div
      className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 p-2 rounded-lg bg-red-50 text-red-600">
          <ExclamationTriangleIcon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Supprimer ce projet ?</h2>
          <p className="text-sm text-gray-600">
            « {project.name} » sera supprimé définitivement, ainsi que ses fichiers et ses évaluations.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
        >
          Supprimer
        </button>
      </div>
    </div>
  </div>
);

export default ProjectWorkspace;
